import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, Star, Clock, Languages, Calendar, Check, BookOpenCheck, BadgeCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import SessionRequestModal from "@/components/SessionRequestModal";
import { api } from "@/lib/api";

const subjectColor = {
  French: "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/50 dark:text-blue-300 dark:border-blue-900",
  English: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/50 dark:text-emerald-300 dark:border-emerald-900",
  Spanish: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/50 dark:text-amber-300 dark:border-amber-900",
  Math: "bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950/50 dark:text-purple-300 dark:border-purple-900",
};

export default function TutorDetail() {
  const { id } = useParams();
  const [tutor, setTutor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookOpen, setBookOpen] = useState(false);

  useEffect(() => {
    api
      .get(`/tutors/${id}`)
      .then((r) => setTutor(r.data))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!tutor) {
    return (
      <div className="max-w-xl mx-auto py-24 text-center">
        <h2 className="text-2xl font-bold">Tutor not found</h2>
        <Link to="/tutors" className="text-primary mt-4 inline-block">← Back to all tutors</Link>
      </div>
    );
  }

  const initial = tutor.name?.slice(0, 1) || "T";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
      <Link to="/tutors" className="text-sm text-muted-foreground hover:text-foreground">
        ← All tutors
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-6 lg:p-8">
            <div className="flex items-start gap-5">
              <Avatar className="h-20 w-20 ring-2 ring-primary/20">
                <AvatarImage src={tutor.photo} alt={tutor.name} />
                <AvatarFallback className="text-2xl font-semibold bg-primary/10 text-primary">
                  {initial}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <h1 className="text-3xl font-bold tracking-tight" data-testid="tutor-detail-name">
                  {tutor.name}
                </h1>
                <p className="text-muted-foreground mt-1">{tutor.headline}</p>
                <div className="flex items-center gap-4 mt-3 text-sm">
                  <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400 font-medium">
                    <Star className="h-4 w-4 fill-current" />
                    {tutor.rating?.toFixed?.(2) ?? tutor.rating}
                    <span className="text-muted-foreground font-normal">({tutor.reviews} reviews)</span>
                  </span>
                  <span className="inline-flex items-center gap-1 text-muted-foreground">
                    <Clock className="h-4 w-4" />{tutor.level}
                  </span>
                  <span className="inline-flex items-center gap-1 text-muted-foreground">
                    <Languages className="h-4 w-4" />{tutor.languages?.join(", ")}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-3 items-center">
                  {tutor.kind === "guide" ? (
                    <Badge
                      variant="outline"
                      className="rounded-full font-medium text-[11px] bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-950/50 dark:text-sky-300 dark:border-sky-900 inline-flex items-center gap-1"
                      data-testid="profile-kind-badge"
                    >
                      <BookOpenCheck className="h-3 w-3" /> Guide
                    </Badge>
                  ) : (
                    <Badge
                      variant="outline"
                      className="rounded-full font-medium text-[11px] bg-primary/10 text-primary border-primary/30 inline-flex items-center gap-1"
                      data-testid="profile-kind-badge"
                    >
                      <BadgeCheck className="h-3 w-3" /> Verified Tutor
                    </Badge>
                  )}
                  {tutor.subjects?.map((s) => (
                    <Badge key={s} variant="outline" className={`rounded-full ${subjectColor[s]}`}>
                      {s}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </Card>

          <Tabs defaultValue="overview">
            <TabsList className="rounded-full">
              <TabsTrigger value="overview" className="rounded-full" data-testid="tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="method" className="rounded-full" data-testid="tab-method">Teaching Method</TabsTrigger>
              <TabsTrigger value="avail" className="rounded-full" data-testid="tab-avail">Availability</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="mt-5">
              <Card className="p-6 lg:p-7">
                <h3 className="font-heading text-xl font-semibold mb-3">About me</h3>
                <p className="text-muted-foreground leading-relaxed">{tutor.bio}</p>
              </Card>
            </TabsContent>

            <TabsContent value="method" className="mt-5">
              <Card className="p-6 lg:p-7">
                <h3 className="font-heading text-xl font-semibold mb-3">How I teach</h3>
                <ul className="space-y-3 text-muted-foreground">
                  {[
                    "Personalized lesson plans based on your current level and goals",
                    "Immersive conversation + structured grammar practice",
                    "Weekly progress reviews with clear, actionable feedback",
                    "Shared notes and homework after every session",
                  ].map((item) => (
                    <li key={item} className="flex gap-3">
                      <Check className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            </TabsContent>

            <TabsContent value="avail" className="mt-5">
              <Card className="p-6 lg:p-7">
                <h3 className="font-heading text-xl font-semibold mb-3 inline-flex items-center gap-2">
                  <Calendar className="h-5 w-5" /> Typical availability
                </h3>
                <div className="flex flex-wrap gap-2">
                  {(tutor.availability?.length ? tutor.availability : ["Weekdays Evenings", "Weekends"]).map((a) => (
                    <Badge key={a} variant="secondary" className="rounded-full font-medium">{a}</Badge>
                  ))}
                </div>
                <p className="text-sm text-muted-foreground mt-4">
                  Request a session and pick the exact day & time that works for you.
                </p>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        <aside className="lg:sticky lg:top-24 lg:self-start">
          <Card className="p-6 border-primary/20 shadow-xl">
            <div className="flex items-baseline gap-1">
              <span className="text-4xl font-heading font-bold">${tutor.hourly_rate}</span>
              <span className="text-muted-foreground">/hr</span>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              First session free · Cancel up to 24h in advance.
            </p>
            <Button
              onClick={() => setBookOpen(true)}
              className="w-full rounded-full mt-5 h-11"
              size="lg"
              data-testid="tutor-detail-book-button"
            >
              Request a session
            </Button>
            <div className="mt-5 pt-5 border-t border-border space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Response time</span><span className="font-medium">under 4h</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Sessions</span><span className="font-medium">{tutor.reviews * 3 + 12}+</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Verified</span><span className="font-medium text-emerald-600">Yes</span></div>
            </div>
          </Card>
        </aside>
      </div>

      <SessionRequestModal tutor={tutor} open={bookOpen} onOpenChange={setBookOpen} />
    </div>
  );
}
